import React from 'react';
import { Camera } from 'lucide-react';

interface UserDetailsProps {
  user: {
    name: string;
    id: string;
    email: string;
    phone: string;
    address: string;
    avatar: string;
  };
}

function profileInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const UserDetails: React.FC<UserDetailsProps> = ({ user }) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center border-b border-zinc-100 pb-6 dark:border-zinc-700 sm:flex-row sm:items-start sm:gap-8">
        <div className="relative shrink-0">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt=""
              className="h-28 w-28 rounded-full border-4 border-white object-cover shadow-md ring-1 ring-zinc-200 dark:border-zinc-700 dark:ring-zinc-600"
            />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-white bg-zinc-200 text-xl font-semibold text-zinc-700 shadow-md ring-1 ring-zinc-200 dark:border-zinc-700 dark:bg-zinc-700 dark:text-zinc-100 dark:ring-zinc-600">
              {profileInitials(user.name)}
            </div>
          )}
          <button
            type="button"
            className="absolute bottom-0 right-0 rounded-full border border-zinc-200 bg-zinc-900 p-2 text-white shadow-md transition-colors hover:bg-zinc-800"
            aria-label="Change photo"
          >
            <Camera className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4 min-w-0 text-center sm:mt-0 sm:flex-1 sm:text-left">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{user.name.trim() ? user.name : '—'}</h3>
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            ID {user.id.trim() ? user.id : '—'}
          </p>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{user.email.trim() ? user.email : '—'}</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { label: 'Email', value: user.email },
          { label: 'Phone', value: user.phone },
        ].map((field) => (
          <div key={field.label} className="rounded-md border border-zinc-200 bg-zinc-50/80 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/80">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{field.label}</p>
            <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">{field.value.trim() ? field.value : '—'}</p>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-zinc-200 bg-zinc-50/80 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/80">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Address</p>
        <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">{user.address.trim() ? user.address : '—'}</p>
      </div>
    </div>
  );
};

export default UserDetails;
